import axios from "axios";

let geoTimerId = null;
let geoWatchId = null;
let lastSentPosition = null;
let bufferedLocationToSend = null;
let lastSentAt = 0;

let geoConfig = {
    tickInterval: 5000, // 5s - interval for buffered location send if it was updated by watch process
    timeout: 10000, // 10 seconds in ms
    watchBufferInterval: 60000, // 1 minute in ms - minimum interval between buffered location updates
    significantDistanceToTrack: 0, // in meters - default 0 means every change is significant
    logAction: "regular check",
    debug: false
};

function isMagnitudeOfChangeSignificant(newCoords, lastCoords, significantDistanceToTrack) {
    if (!lastCoords || !newCoords) return true;
    if (significantDistanceToTrack <= 0) return true;
    
    let delta = approximateDistanceInMeters(
        newCoords.coords.latitude,
        newCoords.coords.longitude,
        lastCoords.coords.latitude,
        lastCoords.coords.longitude
    );
    return delta >= significantDistanceToTrack;
}

/* Using Haversine formula to calculate distance between two points on the Earth - compromise between accuaracy and performance */
function approximateDistanceInMeters(lat1, lon1, lat2, lon2) {

    if (lat1 === lat2 && lon1 === lon2) {
        if (geoConfig.debug) {
            console.log("No distance to calculate, coordinates are the same.");
        }
        return 0;
    }

    const earthRadius = 6371000; // in meters
    const toRad = Math.PI / 180;

    const dLat = (lat2 - lat1) * toRad;
    const dLon = (lon2 - lon1) * toRad;

    const a = 
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    if (geoConfig.debug) {
        console.log(`Calculated distance: ${earthRadius * c} meters`);
    }
    return earthRadius * c;
}

async function saveGeolocationData(logAction = geoConfig.logAction) {
    return new Promise((resolve, reject) => {
        if (!canFetchGeolocation()) {
            if (geoConfig.debug) {
                console.warn("Geolocation cannot be fetched.");
            }
            resolve();
            return;
        }

        if (document.visibilityState === "hidden") {
            if (geoConfig.debug) {
                console.warn("Page is hidden, skipping geolocation update.");
            }
            resolve();
            return;
        }

        navigator.geolocation.getCurrentPosition(
            position => {
                sendGeolocationToBackend(extractPositionData(position), logAction).then(success => {
                    if (success) resolve();
                    else reject("Failed to send geolocation data");
                });
            },
            error => {
                if (error.code === error.PERMISSION_DENIED) {
                    if (geoConfig.debug) {
                        console.warn("Geolocation permission denied, saving empty geolocation.");
                    }
                    sendGeolocationToBackend(extractPositionData(), logAction).then(success => {
                        if (success) resolve();
                        else reject("Failed to send geolocation data");
                    });
                } else {
                    reject(error.message);
                }
            },
            {
                enableHighAccuracy: true,
                timeout: geoConfig.timeout,
                maximumAge: 0
            }
        );
    });
}

function sendGeolocationToBackend(position, logAction) {
    let geolocationData = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        heading: position.coords.heading,
        speed: position.coords.speed,
        altitude: position.coords.altitude,
        altitudeAccuracy: position.coords.altitudeAccuracy,
        timestamp: position.timestamp,
        logAction: logAction
    };

    return axios
        .post(route("geolocation.update").url(), { geolocationData: geolocationData })
        .then((response) => {
            if(response.status === 200) {
                if (geoConfig.debug) {
                    console.log("Geolocation data sent to backend successfully");
                }
                return true;
            } else {
                console.warn("Failed to save geolocation data:", response.data.error);
                return false;
            }
        })
        .catch((err) => {
            console.error("Error sending geolocation data:", err);
            return false;
        });
}


function tickGeolocationTimer(options = {}) {
    let config = {
        ...geoConfig,
        ...options
    };

    const delaySinceLastPositionSent = Date.now() - lastSentAt;

    if(
        delaySinceLastPositionSent >= config.watchBufferInterval
        && bufferedLocationToSend !== null
    ){
        sendGeolocationToBackend(extractPositionData(bufferedLocationToSend), config.logAction)
        .then((success) => {
            lastSentPosition = bufferedLocationToSend;
            bufferedLocationToSend = null;
            lastSentAt = Date.now();
            if (config.debug) {
                console.log("Buffered geolocation data sent successfully, saving last position:", lastSentPosition);
            }
        })
        .catch((err) => {
            if (config.debug) {
                console.warn("Failed to send buffered geolocation data:", err);
            }
        });
    }
}

function extractPositionData(position = null) {
    if (!position) {
        return {
            coords: {
                latitude: null,
                longitude: null,
                accuracy: null,
                heading: null,
                speed: null,
                altitude: null,
                altitudeAccuracy: null
            },
            timestamp: Date.now()
        };
    }

    return {
        coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            heading: position.coords.heading,
            speed: position.coords.speed,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy
        },
        timestamp: position.timestamp || Date.now()
    };
}

function startGeolocationWatchTracking(options = {}) {
    let config = {
        ...geoConfig,
        ...options
    };

    if (typeof config.watchBufferInterval !== "number" || config.watchBufferInterval <= 0) {
        console.warn("Invalid watch interval provided. Geolocation watcher not started.");
        return;
    }

    if (geoWatchId) {
        console.warn("Optimized geolocation watch already running.");
        return;
    }

    if (geoTimerId) {
        console.warn("Geolocation timer is already running.");
        return;
    }

    if (!canFetchGeolocation()) { return; }

    geoTimerId = setInterval(() => {
        tickGeolocationTimer(config);
    }, config.tickInterval);

    if (geoConfig.debug) {
        console.log("Starting geolocation watch with options:", config);
    }
    geoWatchId = navigator.geolocation.watchPosition(
        position => {
            if (geoConfig.debug) {
                console.log("Watch update received:", position);
            }

            if (isMagnitudeOfChangeSignificant(position, lastSentPosition, config.significantDistanceToTrack)) {
                bufferedLocationToSend = position;
                if (geoConfig.debug) {
                    console.log("Significant change detected, buffering location to send:", bufferedLocationToSend);
                }
            }
        },
        error => {
            if (config.debug) {
                console.warn("Geolocation watch error:", error);
            }
        },
        {
            enableHighAccuracy: true,
            timeout: config.timeout,
            maximumAge: 0
        }
    );
}

function stopGeolocationWatchTracking() {
    if (geoWatchId !== null || geoTimerId !== null) {
        navigator.geolocation.clearWatch(geoWatchId);
        clearInterval(geoTimerId);
        geoTimerId = null;
        lastSentPosition = null;
        bufferedLocationToSend = null;
        lastSentAt = 0;
        if (geoConfig.debug) {
            console.log("Geolocation watch stopped.");
        }
    } else {
        if (geoConfig.debug) {
            console.warn("No geolocation watch to stop.");
        }
    }
}

function canFetchGeolocation() {
    if (window.geodataCollectionEnabled === false) {
        if (geoConfig.debug) {
            console.warn("Geolocation collection is disabled.");
        }
        return false;
    }

    if (!navigator.geolocation) {
        if (geoConfig.debug) {
            console.warn("Geolocation API is not supported by this browser.");
        }
        return false;
    }

    return true;
}

export default {
    saveGeolocationData,
    startGeolocationWatchTracking,
    stopGeolocationWatchTracking
};
